import { computed, reactive, ref, unref, watch } from 'vue';
import AgencyProfileAPI from 'dashboard/api/agencyProfile';
import CommissionAgreementsAPI from 'dashboard/api/commissionAgreements';
import CommissionLedgerAPI from 'dashboard/api/commissionLedger';
import ContactAPI from 'dashboard/api/contacts';
import FinanceiroAPI from 'dashboard/api/financeiro';
import ImovelAPI from 'dashboard/api/imoveis';
import PostSaleCasesAPI from 'dashboard/api/postSaleCases';
import PropertyOffersAPI from 'dashboard/api/propertyOffers';
import PropertyTransactionsAPI from 'dashboard/api/propertyTransactions';
import RealEstateBrokerDashboardAPI from 'dashboard/api/realEstateBrokerDashboard';
import RealEstateFinanceAPI from 'dashboard/api/realEstateFinance';
import SalesDashboardAPI from 'dashboard/api/salesDashboard';
import SalesTransactionsAPI from 'dashboard/api/salesTransactions';
import VisitaAPI from 'dashboard/api/visitas';

const MODULES = Object.freeze([
  'sales',
  'proposals',
  'visits',
  'postSale',
  'finance',
  'brokers',
  'commissions',
]);

const emptyArray = Object.freeze([]);
const emptyObject = Object.freeze({});

const responsePayload = response =>
  response?.data?.payload ?? response?.data ?? {};

const collectionFrom = response => {
  const payload = responsePayload(response);
  if (Array.isArray(payload)) return payload;
  return payload?.data || payload?.records || payload?.rows || emptyArray;
};

const metaFrom = response => responsePayload(response)?.meta || emptyObject;

const listingForAgreement = (property, listingId) => {
  if (!property || !listingId) return null;

  const listings = [
    property.primary_listing,
    ...(Array.isArray(property.imovel_listings)
      ? property.imovel_listings
      : emptyArray),
    ...(Array.isArray(property.listings) ? property.listings : emptyArray),
  ];

  return listings.find(listing => `${listing?.id}` === `${listingId}`) || null;
};

const enrichCommissionAgreementProperty = (agreement, property) => {
  if (!agreement || !property) return agreement;

  const listing = listingForAgreement(property, agreement.imovel_listing_id);

  return {
    ...agreement,
    imovel: property,
    ...(listing ? { imovel_listing: listing } : {}),
  };
};

const messageFrom = error =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.response?.data?.errors?.[0] ||
  'Não foi possível atualizar os dados. Tente novamente.';

const routeValue = value => (Array.isArray(value) ? value[0] : value);

const routeParams = route =>
  Object.fromEntries(
    Object.entries(route?.query || {}).filter(
      ([key, value]) => value != null && value !== '' && key !== 'screen'
    )
  );

const isCreateRoute = route =>
  route?.query?.screen === 'create' || route?.name === 'visita_new';

const currentRecordId = (route, module) => {
  const params = route?.params || {};
  const query = route?.query || {};

  const ids = {
    sales: params.salesTransactionId || params.transactionId || query.saleId,
    proposals: params.offerId || params.salesFlowId || query.offerId,
    visits: params.id || params.visitId || query.visitId,
    postSale: params.postSaleCaseId || query.postSaleCaseId,
    finance: params.salesTransactionId || params.financeId || query.financeId,
    brokers: params.brokerId || params.userId || query.brokerId,
    commissions: params.commissionId || params.poolId || query.commissionId,
  };

  return routeValue(ids[module])?.toString() || '';
};

const brokerRecordsFromProfile = profile => {
  const users = Array.isArray(profile?.users) ? profile.users : emptyArray;
  const brokerProfiles = Array.isArray(profile?.corretores)
    ? profile.corretores
    : emptyArray;
  const usersById = new Map(users.map(user => [String(user.user_id), user]));

  const records = brokerProfiles.map(broker => {
    const user = usersById.get(String(broker.user_id)) || emptyObject;
    return {
      ...broker,
      id: broker.user_id || broker.id,
      name: broker.display_name || user.name || '',
      role: user.role || '',
      status: broker.public ? 'Perfil público' : 'Perfil privado',
      user,
    };
  });

  if (records.length) return records;

  return users.map(user => ({
    ...user,
    id: user.user_id || user.account_user_id,
    name: user.name || '',
    role: user.role || '',
    status: 'Membro da equipe',
  }));
};

const createState = () =>
  reactive({
    sales: {
      records: [],
      meta: {},
      dashboard: {},
      detail: null,
      history: [],
      createOptions: { listings: [] },
    },
    proposals: {
      records: [],
      meta: {},
      detail: null,
      timeline: [],
      createOptions: { contacts: [], properties: [] },
    },
    visits: {
      records: [],
      meta: {},
      agenda: [],
      detail: null,
      outcome: null,
      createOptions: { contacts: [], properties: [] },
    },
    postSale: {
      records: [],
      meta: {},
      dashboard: {},
      counts: {},
      detail: null,
      history: [],
      createOptions: { transactions: [] },
    },
    finance: {
      records: [],
      meta: {},
      summary: {},
      charges: [],
      history: [],
      detail: null,
    },
    brokers: {
      records: [],
      meta: {},
      dashboard: {},
      profile: {},
      detail: null,
    },
    commissions: {
      agreements: [],
      agreementMeta: {},
      ledger: [],
      ledgerMeta: {},
      records: [],
      reconciliation: [],
      reconciliationSummary: {},
      summary: {},
      meta: {},
      detail: null,
      createOptions: { listings: [] },
    },
  });

export function useImudeiRealData({ route, module, autoLoad = true } = {}) {
  const state = createState();
  const loading = ref(false);
  const saving = ref(false);
  const error = ref('');
  const lastAction = ref('');
  let requestSequence = 0;

  const activeModule = computed(() => {
    const value = unref(module);
    return MODULES.includes(value) ? value : 'sales';
  });
  const selectedId = computed(() => currentRecordId(route, activeModule.value));
  const data = computed(() => state[activeModule.value]);
  const empty = computed(() => {
    const records = data.value.records || [];
    return !loading.value && !error.value && records.length === 0;
  });

  const loadCreateOptions = async moduleKey => {
    if (!isCreateRoute(route)) return;

    try {
      const [propertiesResponse, contactsResponse] = await Promise.all([
        ImovelAPI.get({ per_page: 6 }),
        ContactAPI.get(1),
      ]);

      state[moduleKey].createOptions = {
        contacts: collectionFrom(contactsResponse).slice(0, 6),
        properties: collectionFrom(propertiesResponse).slice(0, 6),
      };
    } catch {
      state[moduleKey].createOptions = { contacts: [], properties: [] };
    }
  };

  const loadPostSaleCreateOptions = async () => {
    if (!isCreateRoute(route)) return;

    try {
      const response = await SalesDashboardAPI.list({ per_page: 6 });
      state.postSale.createOptions = {
        transactions: collectionFrom(response).slice(0, 6),
      };
    } catch {
      state.postSale.createOptions = { transactions: [] };
    }
  };

  const loadSalesCreateOptions = async () => {
    if (!isCreateRoute(route)) return;

    try {
      const response = await ImovelAPI.get({ per_page: 20 });
      state.sales.createOptions = {
        listings: collectionFrom(response)
          .filter(
            property =>
              property.primary_listing?.id &&
              property.primary_listing?.modalidade === 'venda' &&
              property.primary_listing.available !== false
          )
          .map(property => ({
            ...property,
            id: property.primary_listing.id,
            listing_price:
              property.primary_listing.price_amount || property.valor_venda,
          }))
          .slice(0, 12),
      };
    } catch {
      state.sales.createOptions = { listings: [] };
    }
  };

  const loadCommissionCreateOptions = async () => {
    if (!isCreateRoute(route)) return;

    try {
      const response = await ImovelAPI.get({ per_page: 20 });
      state.commissions.createOptions = {
        listings: collectionFrom(response)
          .map(property => ({
            ...property,
            id:
              property.imovel_listing_id ||
              property.listing_id ||
              property.active_listing_id,
          }))
          .filter(property => property.id)
          .slice(0, 6),
      };
    } catch {
      state.commissions.createOptions = { listings: [] };
    }
  };

  const loadSales = async () => {
    const params = routeParams(route);
    const [listResponse, dashboardResponse] = await Promise.all([
      SalesDashboardAPI.list(params),
      SalesDashboardAPI.dashboard(params),
      loadSalesCreateOptions(),
    ]);
    state.sales.records = collectionFrom(listResponse);
    state.sales.meta = metaFrom(listResponse);
    state.sales.dashboard = responsePayload(dashboardResponse);

    if (!selectedId.value) return;

    const [detailResponse, historyResponse] = await Promise.all([
      SalesTransactionsAPI.show(selectedId.value),
      SalesTransactionsAPI.history(selectedId.value),
    ]);
    state.sales.detail = responsePayload(detailResponse);
    state.sales.history =
      responsePayload(historyResponse)?.events || emptyArray;
  };

  const loadProposals = async () => {
    const [listResponse] = await Promise.all([
      PropertyOffersAPI.get(routeParams(route)),
      loadCreateOptions('proposals'),
    ]);
    state.proposals.records = collectionFrom(listResponse);
    state.proposals.meta = metaFrom(listResponse);

    if (!selectedId.value) return;

    const [detailResponse, timelineResponse] = await Promise.all([
      PropertyOffersAPI.show(selectedId.value),
      PropertyOffersAPI.getTimeline(selectedId.value),
    ]);
    state.proposals.detail = responsePayload(detailResponse);
    const timeline = responsePayload(timelineResponse);
    state.proposals.timeline =
      collectionFrom(timelineResponse).length > 0
        ? collectionFrom(timelineResponse)
        : timeline?.steps ||
          timeline?.timeline ||
          timeline?.events ||
          emptyArray;
  };

  const loadVisits = async () => {
    const params = routeParams(route);
    const [listResponse, agendaResponse] = await Promise.all([
      VisitaAPI.get(params),
      VisitaAPI.agenda(params),
      loadCreateOptions('visits'),
    ]);
    state.visits.records = collectionFrom(listResponse);
    state.visits.meta = metaFrom(listResponse);
    state.visits.agenda = collectionFrom(agendaResponse);

    if (!selectedId.value) return;

    const detailResponse = await VisitaAPI.show(selectedId.value);
    const detail = responsePayload(detailResponse);
    state.visits.detail = detail;
    state.visits.outcome =
      detail.visit_outcome ||
      detail.visitOutcome ||
      detail.commercial_outcome ||
      detail.commercialOutcome ||
      null;
  };

  const loadPostSale = async () => {
    const params = routeParams(route);
    const [listResponse, dashboardResponse, countsResponse] = await Promise.all(
      [
        PostSaleCasesAPI.list(params),
        PostSaleCasesAPI.dashboard(params),
        PostSaleCasesAPI.counts(),
        loadPostSaleCreateOptions(),
      ]
    );
    state.postSale.records = collectionFrom(listResponse);
    state.postSale.meta = metaFrom(listResponse);
    state.postSale.dashboard = responsePayload(dashboardResponse);
    state.postSale.counts = responsePayload(countsResponse);

    if (!selectedId.value) return;

    const [detailResponse, historyResponse] = await Promise.all([
      PostSaleCasesAPI.show(selectedId.value),
      PostSaleCasesAPI.history(selectedId.value),
    ]);
    state.postSale.detail = responsePayload(detailResponse);
    state.postSale.history = collectionFrom(historyResponse);
  };

  const loadFinance = async () => {
    const params = routeParams(route);
    const [listResponse, summaryResponse, chargesResponse, historyResponse] =
      await Promise.all([
        RealEstateFinanceAPI.list(params),
        FinanceiroAPI.resumo(),
        FinanceiroAPI.cobrancas(),
        FinanceiroAPI.historico(params),
      ]);
    state.finance.records = collectionFrom(listResponse);
    state.finance.meta = metaFrom(listResponse);
    state.finance.summary = responsePayload(summaryResponse);
    state.finance.charges = collectionFrom(chargesResponse);
    state.finance.history = collectionFrom(historyResponse);

    if (!selectedId.value) return;

    const detail = responsePayload(
      await RealEstateFinanceAPI.show(selectedId.value)
    );
    state.finance.detail = {
      ...detail,
      endereco: detail.endereco || detail.parcela?.endereco,
      id: detail.id || detail.parcela?.id || selectedId.value,
      manager: detail.manager || detail.dados_processo?.manager,
      processo: detail.processo || detail.dados_processo,
      status: detail.status || detail.parcela?.status,
      valor_parcela: detail.valor_parcela || detail.parcela?.valor_parcela,
    };
  };

  const loadBrokers = async () => {
    const params = routeParams(route);
    const [dashboardResponse, profileResponse] = await Promise.all([
      RealEstateBrokerDashboardAPI.index(params),
      AgencyProfileAPI.get(),
    ]);
    const dashboard = responsePayload(dashboardResponse);
    state.brokers.dashboard = dashboard;
    state.brokers.profile = responsePayload(profileResponse);
    state.brokers.records = brokerRecordsFromProfile(state.brokers.profile);
    state.brokers.meta = {
      ...(dashboard?.meta || emptyObject),
      count: state.brokers.records.length,
    };
    state.brokers.detail =
      state.brokers.records.find(
        record => `${record.id}` === selectedId.value
      ) || null;
  };

  const loadCommissions = async () => {
    const params = routeParams(route);
    const [
      commissionsResponse,
      reconciliationResponse,
      agreementsResponse,
      ledgerResponse,
    ] = await Promise.all([
      FinanceiroAPI.comissoes(params),
      FinanceiroAPI.comissoesReconciliacao(params),
      CommissionAgreementsAPI.list(params),
      CommissionLedgerAPI.list(params),
      loadCommissionCreateOptions(),
    ]);
    const commissionsSummary = responsePayload(commissionsResponse);
    const reconciliation = responsePayload(reconciliationResponse);

    state.commissions.summary = commissionsSummary;
    state.commissions.reconciliation =
      collectionFrom(reconciliationResponse).length > 0
        ? collectionFrom(reconciliationResponse)
        : reconciliation?.pools || emptyArray;
    state.commissions.reconciliationSummary =
      reconciliation?.resumo || emptyObject;
    state.commissions.agreements = collectionFrom(agreementsResponse);
    state.commissions.agreementMeta = metaFrom(agreementsResponse);
    state.commissions.ledger = collectionFrom(ledgerResponse);
    state.commissions.ledgerMeta = metaFrom(ledgerResponse);
    state.commissions.records = state.commissions.agreements;
    state.commissions.meta = state.commissions.agreementMeta;
    state.commissions.detail =
      state.commissions.records.find(
        record => `${record.id || record.pool_id}` === selectedId.value
      ) || null;

    if (!selectedId.value) return;

    const detailResponse = await CommissionAgreementsAPI.show(selectedId.value);
    const agreement = responsePayload(detailResponse);

    if (!agreement?.imovel_id) {
      state.commissions.detail = agreement;
      return;
    }

    try {
      const propertyResponse = await ImovelAPI.show(agreement.imovel_id);
      state.commissions.detail = enrichCommissionAgreementProperty(
        agreement,
        responsePayload(propertyResponse)
      );
    } catch {
      state.commissions.detail = agreement;
    }
  };

  const loaders = {
    sales: loadSales,
    proposals: loadProposals,
    visits: loadVisits,
    postSale: loadPostSale,
    finance: loadFinance,
    brokers: loadBrokers,
    commissions: loadCommissions,
  };

  const refresh = async () => {
    if (!route?.name) return null;

    requestSequence += 1;
    const sequence = requestSequence;
    loading.value = true;
    error.value = '';
    try {
      await loaders[activeModule.value]();
      return data.value;
    } catch (requestError) {
      if (sequence === requestSequence) error.value = messageFrom(requestError);
      return null;
    } finally {
      if (sequence === requestSequence) loading.value = false;
    }
  };

  const runAction = async (name, request) => {
    saving.value = true;
    error.value = '';
    lastAction.value = name;
    try {
      const response = await request();
      await refresh();
      return responsePayload(response);
    } catch (requestError) {
      error.value = messageFrom(requestError);
      throw requestError;
    } finally {
      saving.value = false;
    }
  };

  const requireId = id => id || selectedId.value;

  const actions = Object.freeze({
    sales: {
      create: payload =>
        runAction('createSale', () => PropertyTransactionsAPI.create(payload)),
      saveDraft: (id, profile) =>
        runAction('saveDraft', () =>
          SalesTransactionsAPI.saveDraft(requireId(id), profile)
        ),
      requestReview: (id, items, reason) =>
        runAction('requestReview', () =>
          SalesTransactionsAPI.requestReview(requireId(id), items, reason)
        ),
      resubmitReview: (id, profile) =>
        runAction('resubmitReview', () =>
          SalesTransactionsAPI.resubmitReview(requireId(id), profile)
        ),
      deliver: (id, profile) =>
        runAction('deliver', () =>
          SalesTransactionsAPI.deliver(requireId(id), profile)
        ),
      archive: (id, reason) =>
        runAction('archive', () =>
          SalesTransactionsAPI.archive(requireId(id), reason)
        ),
      restore: id =>
        runAction('restore', () => SalesTransactionsAPI.restore(requireId(id))),
      finalize: id =>
        runAction('finalize', () =>
          SalesTransactionsAPI.finalize(requireId(id))
        ),
    },
    proposals: {
      create: payload =>
        runAction('createProposal', () => PropertyOffersAPI.create(payload)),
      update: (id, payload) =>
        runAction('updateProposal', () =>
          PropertyOffersAPI.update(requireId(id), payload)
        ),
      accept: (id, payload) =>
        runAction('acceptProposal', () =>
          PropertyOffersAPI.accept(requireId(id), payload)
        ),
      decline: (id, payload) =>
        runAction('declineProposal', () =>
          PropertyOffersAPI.decline(requireId(id), payload)
        ),
      withdraw: (id, payload) =>
        runAction('withdrawProposal', () =>
          PropertyOffersAPI.withdraw(requireId(id), payload)
        ),
      counter: (id, payload) =>
        runAction('counterProposal', () =>
          PropertyOffersAPI.counter(requireId(id), payload)
        ),
    },
    visits: {
      create: payload =>
        runAction('createVisit', () => VisitaAPI.create(payload)),
      update: (id, payload) =>
        runAction('updateVisit', () =>
          VisitaAPI.update(requireId(id), payload)
        ),
      confirm: id =>
        runAction('confirmVisit', () =>
          VisitaAPI.update(requireId(id), { status: 'confirmada' })
        ),
      checkIn: id =>
        runAction('checkInVisit', () => VisitaAPI.checkIn(requireId(id))),
      finish: (id, payload) =>
        runAction('finishVisit', () =>
          VisitaAPI.finish(requireId(id), payload)
        ),
      finalizeOutcome: (id, payload) =>
        runAction('finalizeVisitOutcome', () =>
          VisitaAPI.finalizeOutcome(requireId(id), payload)
        ),
      reschedule: (id, payload) =>
        runAction('rescheduleVisit', () =>
          VisitaAPI.reschedule(requireId(id), payload)
        ),
      issueFeedbackToken: (id, payload) =>
        runAction('issueVisitFeedbackToken', () =>
          VisitaAPI.issueFeedbackToken(requireId(id), payload)
        ),
      approveConfirmationAudit: (id, payload) =>
        runAction('approveVisitConfirmationAudit', () =>
          VisitaAPI.approveConfirmationAudit(requireId(id), payload)
        ),
      rejectConfirmationAudit: (id, payload) =>
        runAction('rejectVisitConfirmationAudit', () =>
          VisitaAPI.rejectConfirmationAudit(requireId(id), payload)
        ),
    },
    postSale: {
      create: payload =>
        runAction('createPostSaleCase', () => PostSaleCasesAPI.create(payload)),
      transition: (id, payload) =>
        runAction('transitionPostSaleCase', () =>
          PostSaleCasesAPI.transition(requireId(id), payload)
        ),
      complete: id =>
        runAction('completePostSaleCase', () =>
          PostSaleCasesAPI.complete(requireId(id))
        ),
      cancel: (id, payload) =>
        runAction('cancelPostSaleCase', () =>
          PostSaleCasesAPI.cancel(requireId(id), payload)
        ),
      assign: (id, payload) =>
        runAction('assignPostSaleCase', () =>
          PostSaleCasesAPI.assign(requireId(id), payload)
        ),
      addNote: (id, payload) =>
        runAction('addPostSaleNote', () =>
          PostSaleCasesAPI.addNote(requireId(id), payload)
        ),
    },
    finance: {
      transition: (id, payload) =>
        runAction('transitionFinance', () =>
          RealEstateFinanceAPI.transition(requireId(id), payload)
        ),
      confirmPayment: (id, responsibilityId, payload) =>
        runAction('confirmPayment', () =>
          RealEstateFinanceAPI.confirmPayment(
            requireId(id),
            responsibilityId,
            payload
          )
        ),
      closePeriod: payload =>
        runAction('closeFinancePeriod', () =>
          FinanceiroAPI.fecharPeriodo(payload)
        ),
    },
    brokers: {
      updateProfile: payload =>
        runAction('updateAgencyProfile', () =>
          AgencyProfileAPI.update(payload)
        ),
      createBranch: payload =>
        runAction('createAgencyBranch', () =>
          AgencyProfileAPI.createBranch(payload)
        ),
      createLicense: payload =>
        runAction('createAgencyLicense', () =>
          AgencyProfileAPI.createLicense(payload)
        ),
      createTerritory: payload =>
        runAction('createAgencyTerritory', () =>
          AgencyProfileAPI.createTerritory(payload)
        ),
    },
    commissions: {
      markPayable: (poolId, payload) =>
        runAction('markCommissionPayable', () =>
          FinanceiroAPI.marcarComissaoPayable(poolId, payload)
        ),
      markPaid: (poolId, payload) =>
        runAction('markCommissionPaid', () =>
          FinanceiroAPI.marcarComissaoPaid(poolId, payload)
        ),
      createManual: payload =>
        runAction('createManualCommission', () =>
          FinanceiroAPI.lancarComissaoManual(payload)
        ),
      createAgreement: payload =>
        runAction('createCommissionAgreement', () =>
          CommissionAgreementsAPI.create(payload)
        ),
      publishAgreement: id =>
        runAction('publishCommissionAgreement', () =>
          CommissionAgreementsAPI.publish(requireId(id))
        ),
    },
  });

  watch(
    () => [unref(autoLoad), activeModule.value, route?.fullPath],
    ([enabled]) => {
      if (enabled) refresh();
    },
    { immediate: true }
  );

  return {
    actions,
    data,
    empty,
    error,
    lastAction,
    loading,
    module: activeModule,
    refresh,
    saving,
    selectedId,
  };
}
